/**
 * ACT2-1108: "Tweak your workspace settings". The same switches, words and writes as the Settings pages:
 * the four Context › AI controls, Slack "Guest access" (only when Slack is connected), and public
 * dashboard sharing. Each switch saves as it is flipped, one save at a time.
 */
import { useQuery } from '@tanstack/react-query';
import { useId } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { Switch } from '@/_shared/components/ui/switch';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import {
  useAiControlsMutation,
  usePublicSharingMutation,
  useSlackGuestAccessMutation,
} from '@/settings/hooks/useSettingsSwitchMutations';
import { AI_CONTROLS, PUBLIC_SHARING, SLACK_GUEST_ACCESS } from '@/settings/lib/settingsSwitches';

import { SETUP_PANEL_TOAST } from '../lib/panelToast';

function SwitchRow({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}): React.ReactElement {
  return (
    <div className="flex items-start justify-between gap-3 py-2">
      <div className="min-w-0">
        {/* Tied by id: a wrapping <label> does not name the button the Switch renders. */}
        <label htmlFor={id} className="block text-sm text-foreground">
          {title}
        </label>
        <p id={`${id}-description`} className="mt-0.5 text-xs text-muted-foreground">
          {description}
        </p>
      </div>
      <Switch
        id={id}
        aria-describedby={`${id}-description`}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
      />
    </div>
  );
}

function GroupHeading({ children }: { children: React.ReactNode }): React.ReactElement {
  return <h4 className="pt-2 text-xs font-bold tracking-wider text-muted-foreground uppercase">{children}</h4>;
}

export function SettingsStep({ onDone }: { onDone: () => void }): React.ReactElement {
  const orpc = useOrpc();
  const { wp } = useWorkspace();
  const idPrefix = useId();
  const { data, isPending, error } = useQuery(orpc.settings.get.queryOptions({ input: {} }));
  const { data: slack } = useQuery(orpc.slack.getInstallStatus.queryOptions({ input: {} }));
  const aiControls = useAiControlsMutation(SETUP_PANEL_TOAST);
  const publicSharing = usePublicSharingMutation(SETUP_PANEL_TOAST);
  const guestAccess = useSlackGuestAccessMutation(SETUP_PANEL_TOAST);

  if (isPending) {
    return (
      <div role="status" aria-label="Loading settings">
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }
  if (!data) return <Callout variant="error">{error?.message ?? 'Failed to load settings.'}</Callout>;

  const { settings } = data;
  // Every save rewrites all the workspace flags on the server (`saveWorkspaceSettings`), so two saves in
  // flight can undo each other. While any one is pending, every switch waits.
  const saving = aiControls.isPending || publicSharing.isPending || guestAccess.isPending;
  return (
    <div className="flex flex-col gap-1">
      <GroupHeading>Context Hub · AI controls</GroupHeading>
      {AI_CONTROLS.map((control) => (
        <SwitchRow
          key={control.key}
          id={`${idPrefix}-${control.key}`}
          title={control.title}
          description={control.description}
          checked={settings[control.key]}
          disabled={saving}
          // Only the flag this switch changes, as the Settings page sends it.
          onCheckedChange={(checked) => aiControls.mutate({ [control.key]: checked })}
        />
      ))}
      {slack?.installed === true && (
        <>
          <GroupHeading>Slack</GroupHeading>
          <SwitchRow
            id={`${idPrefix}-slack-guest`}
            title={SLACK_GUEST_ACCESS.title}
            description={SLACK_GUEST_ACCESS.description}
            checked={settings.slackGuestAccessEnabled}
            disabled={saving}
            onCheckedChange={(checked) => guestAccess.mutate({ slackGuestAccessEnabled: checked })}
          />
        </>
      )}
      <GroupHeading>Dashboards</GroupHeading>
      <SwitchRow
        id={`${idPrefix}-public-sharing`}
        title={PUBLIC_SHARING.title}
        description={PUBLIC_SHARING.description}
        checked={settings.publicSharingEnabled}
        disabled={saving}
        onCheckedChange={(checked) => publicSharing.mutate({ publicSharingEnabled: checked })}
      />
      <div className="mt-2 flex flex-wrap items-center gap-2 p-0.5">
        <Button type="button" variant="primary" label="Looks good" onClick={onDone} />
        <Button asChild variant="tertiary">
          <Link to={wp('/settings')}>Open all settings</Link>
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Changes save as you switch them. You can change them later in Settings.
      </p>
    </div>
  );
}
